import React, { Suspense, useEffect, useState } from 'react'
import { Canvas, useFrame } from '@react-three/fiber'
import { Physics } from '@react-three/rapier';
import { Loader, OrbitControls, PerspectiveCamera, useProgress } from "@react-three/drei";
import { EcctrlJoystick } from "ecctrl";
import { ACESFilmicToneMapping } from 'three';

import Game from './Game.tsx';
import DiscussionGUI from './ui/DiscussionGUI.tsx';
import QuestGUI from './ui/QuestGUI.tsx';
import { QuestSuccessOverlay } from './ui/QuestSuccessOverlay.tsx';
import PlayerHintGUI from './ui/PlayerHintGUI.tsx';
import LetterGUI from './ui/LetterGUI.tsx';
import { useGameController } from './hooks/useGameController.ts';
import { useUtils } from './hooks/useUtils.ts';
import Menu from './Menu.tsx';
import MenuScene from './MenuScene.tsx';
import { Vector3 } from 'three';
import BookGUI from './ui/BookGUI.tsx';
import PlayerGUI from './ui/PlayerGUI.tsx';
import { usePlausible } from './hooks/usePlausible.ts';
import MapGUI from './ui/MapGUI.tsx';
import LoginModal from './ui/LoginModal.tsx';
import { useAuthStore } from './stores/auth.ts';
import { SleepGUI } from './ui/SleepGUI.tsx';
import ShopGUI from './ui/ShopGUI.tsx';
import InventoryGUI from './ui/InventoryGUI.tsx';
import { DeathGUI } from './ui/DeathGUI.tsx';


export default function App() {
  const { mobileAndTabletCheck } = useUtils();
  const plausible = usePlausible();
  const { active, progress, errors, item, loaded, total } = useProgress()
  const gameController = useGameController();
  const [gameStatus, setGameStatus] = useState(gameController.getGameState().status);
  const authState = useAuthStore();
  const cameraRef = React.useRef();
  useEffect(()=>{
    if(mobileAndTabletCheck()){
      try {
        document.body.requestFullscreen();
      } catch (error) {
        console.log("Fullscreen not supported");
      } 
    }
    authState.load();
    function handler (event, gameState) {
      setGameStatus(gameState.status);
    }
    function analyticsHandler(event, data){
      if(plausible){
        if(event == "game:status:updated" || event == "scene:loaded"){
          plausible("SceneLoaded", {props: {scene: data.sceneKey}});
        } else if (event == "task:completed"){
          plausible("TaskCompleted", {props: {task: data.title}});
        }
        
      }
    }
    gameController.addEventHandler(["game:status:updated"], 'app', handler);
    gameController.addEventHandler(["scene:loaded", "game:status:updated", "task:completed"], 'app', analyticsHandler);

    return ()=>{
      gameController.removeEventHandler(["game:status:updated"],'app', handler);
      gameController.removeEventHandler(["scene:loaded", "game:status:updated", "task:completed"],'app', analyticsHandler);
    }
  }, []);
  
  function onCanvasClick(){
    if(mobileAndTabletCheck()){
      gameController.sendEvent('player:interacted');
    }
  }
  
  function handleContextMenu(e) {
    e.preventDefault();
  }
  
  function handleDragStart(e) {
    e.preventDefault();
  }
  
  // Add event listeners to prevent canvas dragging
  useEffect(() => {
    const canvas = document.getElementById('game-canvas');
    if (canvas) {
      const preventDrag = (e) => {
        e.preventDefault();
        return false;
      };
      
      const preventSelect = (e) => {
        e.preventDefault();
        return false;
      };
      
      canvas.addEventListener('dragstart', preventDrag);
      canvas.addEventListener('selectstart', preventSelect);
      canvas.addEventListener('dragend', preventDrag);
      canvas.addEventListener('drag', preventDrag);
      
      // Also prevent on the actual canvas element inside
      const canvasElement = canvas.querySelector('canvas');
      if (canvasElement) {
        canvasElement.addEventListener('dragstart', preventDrag);
        canvasElement.addEventListener('selectstart', preventSelect);
        canvasElement.addEventListener('dragend', preventDrag);
        canvasElement.addEventListener('drag', preventDrag);
      }
      
      return () => {
        canvas.removeEventListener('dragstart', preventDrag);
        canvas.removeEventListener('selectstart', preventSelect);
        canvas.removeEventListener('dragend', preventDrag);
        canvas.removeEventListener('drag', preventDrag);
        
        if (canvasElement) {
          canvasElement.removeEventListener('dragstart', preventDrag);
          canvasElement.removeEventListener('selectstart', preventSelect);
          canvasElement.removeEventListener('dragend', preventDrag);
          canvasElement.removeEventListener('drag', preventDrag);
        }
      };
    }
  }, [gameStatus]); // Re-run when game status changes
  
  return (
    <>
    <LoginModal/>
    { gameStatus === 'menu' ? 
      (progress == 100 ? <Menu/> : null) :
      <>
        { gameController.settings.gui.player && <PlayerGUI/> }
        { gameController.settings.gui.quest && <QuestSuccessOverlay/> }
        { gameController.settings.gui.quest && <QuestGUI/> }
        { gameController.settings.gui.discussion && <DiscussionGUI/> }
        { gameController.settings.gui.hint && <PlayerHintGUI/> }
        { gameController.settings.gui.shop && <ShopGUI/> }
        { gameController.settings.gui.letter && <LetterGUI/> }
        { gameController.settings.gui.book && <BookGUI/> }
        { gameController.settings.gui.map && <MapGUI/> }
        { gameController.settings.gui.sleep && <SleepGUI/> }
        { gameController.settings.gui.inventory && <InventoryGUI/> }
        { gameController.settings.gui.death && <DeathGUI/> }
        { mobileAndTabletCheck() ? <EcctrlJoystick /> : null }
      </> 
    }
      <Canvas 
        id="game-canvas" 
        onClick={()=>onCanvasClick()} 
        onContextMenu={handleContextMenu}
        onDragStart={handleDragStart}
        shadows 
        gl={{
          antialias: true,
          toneMapping: ACESFilmicToneMapping,
          toneMappingExposure: 1.2,
          outputColorSpace: 'srgb'
        }}
        onCreated={({ gl }) => {
          gl.shadowMap.enabled = true;
          gl.shadowMap.type = 2; // PCFSoftShadowMap
        }}
        style={{backgroundColor: gameStatus === 'menu' && progress == 100 ? '#3498db' : 'transparent'}}
      >
        <Suspense>
        <Physics debug={gameController.settings.debug}>
          { gameStatus === 'menu' ? <MenuScene/> : <Game camera={cameraRef}/> }
        </Physics>
        </Suspense>
        {/*
            Use a PerspectiveCamera.
            PerspectiveCameras work like real works cameras
            and provide depth perception.
          */}
          <PerspectiveCamera ref={cameraRef}  makeDefault position={[1,3,1]}/>
          {/*
            This lets you rotate the camera.
            We've associated our React ref with it
            like we would do for any React component
          */}
          { /*<OrbitControls />*/}
      </Canvas>
      <Loader initialState={(active) => active}/>
      </>)}
